type GoogleMapsLoaderState = {
  promise?: Promise<void>;
  apiKey?: string;
};

const getLoaderState = (): GoogleMapsLoaderState => {
  const w = window as unknown as { __googleMapsApiLoader?: GoogleMapsLoaderState };
  if (!w.__googleMapsApiLoader) {
    w.__googleMapsApiLoader = {};
  }
  return w.__googleMapsApiLoader;
};

export const loadGoogleMapsApi = (apiKey: string): Promise<void> => {
  if (!apiKey) {
    return Promise.reject(new Error('Missing Google Maps API key'));
  }

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps API can only load in the browser'));
  }

  const ensureLibrariesLoaded = () => {
    if (!window.google || !window.google.maps) return Promise.reject(new Error('Google Maps API not ready'));
    if (typeof window.google.maps.Map === 'function') return Promise.resolve();
    if (typeof window.google.maps.importLibrary === 'function') {
      return Promise.all([
        window.google.maps.importLibrary('maps').catch(() => null),
        window.google.maps.importLibrary('marker').catch(() => null),
        window.google.maps.importLibrary('geometry').catch(() => null)
      ]).then(() => undefined);
    }
    return Promise.reject(new Error('Google Maps API not ready'));
  };

  if (window.google && window.google.maps) {
    return ensureLibrariesLoaded();
  }

  const loader = getLoaderState();
  if (loader.promise) {
    if (loader.apiKey && loader.apiKey !== apiKey) {
      console.warn('Google Maps API already loading with a different key. Reusing existing script.');
    }
    return loader.promise;
  }

  const existingScript = document.querySelector('script[data-google-maps-api="true"]') as HTMLScriptElement | null;
  if (existingScript) {
    loader.promise = new Promise<void>((resolve, reject) => {
      const waitForMaps = () => {
        if (window.google && window.google.maps) {
          ensureLibrariesLoaded().then(resolve).catch(() => setTimeout(waitForMaps, 50));
        } else {
          setTimeout(waitForMaps, 50);
        }
      };
      existingScript.addEventListener('load', waitForMaps, { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps API')), { once: true });
      waitForMaps();
    });
    return loader.promise;
  }

  loader.apiKey = apiKey;
  loader.promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.setAttribute('data-google-maps-api', 'true');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,marker&v=weekly&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const waitForMaps = () => {
        if (window.google && window.google.maps) {
          ensureLibrariesLoaded().then(resolve).catch(() => setTimeout(waitForMaps, 50));
        } else {
          setTimeout(waitForMaps, 50);
        }
      };
      waitForMaps();
    };
    script.onerror = () => {
      loader.promise = undefined;
      reject(new Error('Failed to load Google Maps API'));
    };
    document.head.appendChild(script);
  });

  return loader.promise;
};
